/**
 * Where a Hiring candidate lands when they sign in (DA-5042 C-9).
 *
 * A candidate is a real Dosen user, deliberately, so that hiring them keeps
 * their account and the levels they earned (PRD 6.3). The cost is that after
 * they sign up through the Auth0 invitation, the app's normal redirect would
 * drop them on a dashboard where the backend refuses every call: they would see
 * a screen of errors instead of their assessment.
 *
 * So RoleRedirect asks this first. The backend's endpoint allow-list is what
 * actually protects the product (PRD 6.1); this is only what stops a candidate
 * seeing a broken page.
 *
 * It asks the server only when the signed-in user COULD be a candidate: no
 * program membership, no org-level role and no admin role. A candidate always
 * matches that, and a newly invited member does too, so the question has to be
 * one the server answers with a 200 either way: GET /v3/hiring/me returns
 * `{ candidate: false }` rather than refusing. It used to refuse, and that put
 * a 4xx in the middle of a normal sign-in for those users, which broke the
 * first step of two unrelated journeys on 26 Oct 2026 (MOB-01, TRL-02).
 */
import { useEffect } from 'react';
import { useAppSelector } from '../redux/hooks';
import { forgetCandidateRefusal, isCandidate, wasRefusedAsCandidate } from '../api/hiring';
import { useHiringMe } from '../queries/useHiring';
import ROLES from '../utils/roles';

export type CandidateLanding = 'no' | 'asking' | 'candidate';

/**
 * A candidate must never go through the member first-time setup. That screen
 * asks for attribute lists and a member type ("This is how you'll appear to
 * others in your program"), which is a learner view a candidate may not see
 * (C-9) and which would make them match attribute audiences like a member
 * (PRD 6.2). Their onboarding is the round's own: residence, the notice, then
 * the fields the owner chose.
 *
 * SecuredRoutes and Redirect both consult this before sending anyone to the
 * FTUE, and they have to agree: SecuredRoutes bounces /hiring to /, and
 * Redirect would send a candidate back to /hiring, so if only one of them knew
 * about candidates the two would bounce the page between them forever.
 */
export const isCandidateRoute = (): boolean =>
  (typeof window !== 'undefined' ? window.location.hash || '' : '').startsWith('#/hiring');

export const useHiringCandidateLanding = (): CandidateLanding => {
  const profile = useAppSelector((state) => state.userProfile?.data) as
    | { groupsAll?: string[]; orgRoles?: string[]; admins?: string[] }
    | undefined;
  const role = localStorage.getItem('role') || '';
  // NOT "has no org role": a candidate's invitation carries the same
  // `role: ["mentee"]` as any org-level invitation, so it lands on them as
  // orgRoles too, and a check for an empty orgRoles would never recognise the
  // very people it exists for. Found on 26 Oct 2026, by walking the candidate
  // flow by hand: the first candidate was pushed into the member first-time
  // setup because of exactly that.
  //
  // What a candidate never has is a PROGRAM. Someone invited to the org who has
  // not been enrolled yet has none either, so they pay one extra 200 on
  // sign-in; that is the whole cost of being sure.
  //
  // Startup is also allowed to say so outright. A candidate's first page load
  // is refused `userBasicSettings` before the profile is ever fetched, so for
  // them the profile never arrives and the two tests below would wait forever.
  // That refusal only earns them the question; the server's answer still
  // decides.
  const refused = wasRefusedAsCandidate();
  const couldBeCandidate =
    refused ||
    (Boolean(profile) &&
      (profile?.groupsAll || []).length === 0 &&
      (profile?.admins || []).length === 0 &&
      role !== ROLES.orgAdmin &&
      role !== ROLES.platAdmin &&
      role !== ROLES.grpAdmin);

  const me = useHiringMe(couldBeCandidate);
  // Not knowing yet is not the same as "no". Both callers consult this on a
  // path that already shows a loader, and answering "no" while the profile is
  // still in flight would race a candidate into the member first-time setup,
  // which is the one place they must never reach.
  const answered = !me.isLoading && me.data !== undefined;
  // A hired candidate becomes an ordinary member. The moment the server says
  // this account is not a candidate, the startup refusal that made us ask is
  // wrong and has to go, or it would keep the rest of the app out all session.
  useEffect(() => {
    if (answered && !isCandidate(me.data)) forgetCandidateRefusal();
  }, [answered, me.data]);

  if (!profile && !refused) return 'asking';
  if (!couldBeCandidate) return 'no';
  if (me.isLoading) return 'asking';
  return isCandidate(me.data) ? 'candidate' : 'no';
};
